import { Database, FileText, Shield } from "lucide-react";
import { motion } from "motion/react";
import { fadeUpItem, staggerContainer } from "./homepageMotion";
import { GlassPanel, MotionSection, SweepButton } from "./HomepagePrimitives";

type QuoteCtaProps = {
  onRequestQuote?: () => void;
};

/**
 * Security section in a stark dark theme.
 * @returns Security section
 */
export const HomepageSecuritySection = () => {
  const pillars = [
    { title: "Bank-Grade Encryption", icon: <Shield className="tb-loop-icon h-6 w-6 text-(--brand-primary)" /> },
    {
      title: "Isolated Databases per Client",
      icon: <Database className="tb-loop-icon h-6 w-6 text-(--brand-primary)" />,
    },
    { title: "Signed NDA & Non-Compete", icon: <FileText className="tb-loop-icon h-6 w-6 text-(--brand-primary)" /> },
  ];

  return (
    <MotionSection id="security" className="border-t border-gray-800 bg-[#131B2A] px-4 py-20 sm:px-6 lg:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-balance text-4xl font-semibold tracking-normal text-white sm:text-5xl lg:text-[3.2rem] lg:leading-tight">
            Your data, protected. Always.
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-gray-400 sm:text-lg">
            We don&apos;t just secure your data; we guarantee we&apos;ll never use it.
          </p>
        </div>

        <motion.div
          className="mt-14 grid gap-5 lg:grid-cols-3"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.24 }}
          variants={staggerContainer}
        >
          {pillars.map((pillar) => (
            <motion.article key={pillar.title} variants={fadeUpItem}>
              <GlassPanel tone="dark" innerClassName="p-7" hoverGlow>
                <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-white/10 bg-white/5">
                  {pillar.icon}
                </div>
                <h3 className="mt-6 max-w-[16ch] text-2xl font-semibold tracking-normal text-white">{pillar.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-gray-400">
                  Enterprise-grade privacy controls designed for client-sensitive itineraries, pricing, and supplier
                  records.
                </p>
              </GlassPanel>
            </motion.article>
          ))}
        </motion.div>

        <div className="mt-12 flex justify-center">
          <a
            href="/security"
            className="tb-loop-shine inline-flex min-h-12 items-center justify-center rounded-lg border border-(--brand-primary) px-6 text-sm font-semibold !text-(--brand-primary) transition-colors duration-200 hover:bg-white/5"
          >
            Read our Full Security Commitment
          </a>
        </div>
      </div>
    </MotionSection>
  );
};

/**
 * Testimonials section with a structured two-card layout.
 * @returns Testimonials section
 */
export const HomepageTestimonialsSection = () => {
  const testimonials = [
    {
      quote:
        "TripboxCRM gave our consultants one place to manage quotes, suppliers, and collections. We finally feel faster than our market.",
      name: "Aarav Mehta",
      role: "CEO, Travel Agency",
    },
    {
      quote:
        "The itinerary builder and payment visibility alone saved us hours per booking. The system feels built by people who understand real travel operations.",
      name: "Nina Joseph",
      role: "CEO, Travel Agency",
    },
  ];

  return (
    <MotionSection id="testimonials" className="border-t border-gray-200 bg-white px-4 py-20 sm:px-6 lg:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-balance text-4xl font-semibold tracking-normal text-[#131B2A] sm:text-5xl lg:text-[3.2rem] lg:leading-tight">
            Don&apos;t just take our word for it.
          </h2>
        </div>

        <motion.div
          className="mt-14 grid gap-5 lg:grid-cols-2"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          {testimonials.map((testimonial) => (
            <motion.article key={testimonial.name} variants={fadeUpItem}>
              <GlassPanel innerClassName="p-8" hoverGlow>
                <div className="flex items-center gap-2 text-lg text-(--brand-primary)">
                  <span>★</span>
                  <span className="tb-loop-pulse">★</span>
                  <span>★</span>
                  <span className="tb-loop-pulse" style={{ animationDelay: "0.3s" }}>
                    ★
                  </span>
                  <span>★</span>
                </div>
                <p className="mt-6 max-w-[32ch] text-2xl leading-relaxed tracking-normal text-gray-600">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                <div className="mt-9 border-t border-gray-200 pt-5">
                  <p className="text-base font-semibold text-[#131B2A]">{testimonial.name}</p>
                  <p className="mt-1 text-sm text-[#131B2A]/52">{testimonial.role}</p>
                </div>
              </GlassPanel>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </MotionSection>
  );
};

/**
 * Final CTA section with a stark dark container.
 * @param props Optional quote CTA handler
 * @returns Final CTA section
 */
export const HomepageFinalCtaSection = ({ onRequestQuote }: QuoteCtaProps) => {
  return (
    <MotionSection id="final-cta" className="bg-white px-4 pb-20 sm:px-6 lg:pb-24">
      <div className="mx-auto max-w-6xl">
        <motion.div
          className="tb-loop-shine rounded-2xl border border-[#131B2A] bg-[#131B2A] px-8 py-12 sm:px-10 lg:px-14 lg:py-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.28 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        >
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mx-auto max-w-6xl text-balance text-4xl font-semibold tracking-normal text-white sm:text-5xl lg:text-[3.4rem] lg:leading-tight">
              Ready to scale your travel business without the operational headaches?
            </h2>
            <p className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-gray-400 sm:text-lg">
              Get a personalized platform walkthrough and a pricing plan tailored perfectly to your team&apos;s size and
              needs.
            </p>
            <div className="mt-10 flex justify-center">
              <SweepButton
                href="#footer"
                tone="gold"
                className="min-h-12 px-6"
                onClick={
                  onRequestQuote
                    ? (event) => {
                        event.preventDefault();
                        onRequestQuote();
                      }
                    : undefined
                }
              >
                Get My Custom Quote
              </SweepButton>
            </div>
          </div>
        </motion.div>
      </div>
    </MotionSection>
  );
};

/**
 * Dark footer for the TripboxCRM homepage.
 * @returns Footer section
 */
export const HomepageFooterSection = () => {
  const footerLinkClass =
    "block !text-(--brand-primary) no-underline transition-colors duration-150 hover:!text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary) focus-visible:ring-offset-2 focus-visible:ring-offset-[#131B2A]";

  return (
    <footer id="footer" className="border-t border-white/10 bg-[#131B2A] px-4 py-16 text-white sm:px-6">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-10 border-b border-white/10 pb-10 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <img alt="TripboxCRM" src="/logo_text.png" className="h-9 w-auto" />
            <p className="mt-5 max-w-[28ch] text-sm leading-7 text-white/62">
              Premium operating software for modern travel teams managing sales, itineraries, ops, and collections.
            </p>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-white/48">Product</h3>
            <div className="mt-5 space-y-3 text-sm">
              <a href="/features" className={footerLinkClass}>
                Features
              </a>
              <a href="/#integrations" className={footerLinkClass}>
                Integrations
              </a>
              <a href="/security" className={footerLinkClass}>
                Security
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-white/48">Company</h3>
            <div className="mt-5 space-y-3 text-sm">
              <a href="/about" className={footerLinkClass}>
                About
              </a>
              <a href="/#testimonials" className={footerLinkClass}>
                Testimonials
              </a>
              <a href="/privacy-policy" className={footerLinkClass}>
                Privacy Policy
              </a>
              <a href="/terms-conditions" className={footerLinkClass}>
                Terms & Conditions
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-white/48">Contact</h3>
            <div className="mt-5 space-y-3 text-sm text-white/72">
              <p>hello@tripboxcrm.com</p>
              <p>+91 97697 23417</p>
              <p>Mumbai, India</p>
            </div>
          </div>
        </div>

        <div className="pt-6 text-sm text-white/50">
          © 2026 TripboxCRM. Built by the Travel Trade, for the Travel Trade.
        </div>
      </div>
    </footer>
  );
};
